import { useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useCreateReminder, useReminders } from '../hooks/useReminders';
import { ReminderForm, type ReminderFormValues } from '../components/reminders/ReminderForm';
import { ReminderRow } from '../components/reminders/ReminderRow';
import { SkeletonTable } from '../components/ui/Skeleton';

/**
 * "Remindere" — the caller's private reminders, Google-Tasks-style: one narrow
 * card, an add row that expands into the inline form, open reminders soonest
 * first and a collapsible "Finalizate" section. Strictly personal — the API
 * only ever returns the caller's rows.
 */
export function RemindersPage() {
  const { t } = useI18n();
  const { data, isLoading, isError } = useReminders();
  const create = useCreateReminder();
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const reminders = data?.reminders ?? [];
  const open = reminders.filter((r) => !r.done);
  const done = reminders.filter((r) => r.done);

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <h1 className="text-lg font-semibold text-foreground">{t('reminders.title')}</h1>
        </div>

        {isLoading && (
          <div className="p-4">
            <SkeletonTable rows={4} columns={1} />
          </div>
        )}
        {isError && <p className="px-4 py-6 text-sm text-red-600">{t('reminders.error')}</p>}

        {!isLoading && !isError && (
          <>
            {adding ? (
              <div className="border-b border-border bg-accent/30">
                <ReminderForm
                  busy={create.isPending}
                  onCancel={() => setAdding(false)}
                  onSave={(values: ReminderFormValues) =>
                    create.mutate(values, { onSuccess: () => setAdding(false) })
                  }
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left text-[14px] text-primary hover:bg-accent/50"
              >
                <Plus size={18} />
                <span>{t('reminders.add')}</span>
              </button>
            )}

            {open.length === 0 && !adding && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                {t('reminders.empty')}
              </p>
            )}
            {open.map((reminder) => (
              <ReminderRow key={reminder.id} reminder={reminder} />
            ))}

            {done.length > 0 && (
              <div className="border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowDone((v) => !v)}
                  aria-expanded={showDone}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] font-medium text-muted-foreground hover:bg-accent/50"
                >
                  {showDone ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  <span>{t('reminders.completed', { count: String(done.length) })}</span>
                </button>
                {showDone &&
                  done.map((reminder) => <ReminderRow key={reminder.id} reminder={reminder} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
